'use client'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { formatPublishedSaleAsk } from '@/lib/listing/publish-listing-ask'
import {
  publishListingShareKind,
  publishListingSharePricePerSqft,
} from '@/lib/listing/publish-listing-share'
import { V3_ROOT_CLASS } from '@/components/site/v3/atoms'
import { SplitCardMedia, SPLIT_CARD_MEDIA_SIZES_SPLIT } from '@/components/site/v3/SplitCardMedia'
import type { V3ListingRowBadge } from '@/components/site/v3/V3ListingRow'
import type { PpsfBand } from '@/components/search/ppsf-band'
import { SearchCompareMark } from '@/app/search/_v3/SearchCompareMark'
import '@/components/site/v3/V3ListingRow.css'
import './search-ledger.css'

export function SplitListingCard({
  listingKey,
  href,
  photoUrls,
  price,
  addressLine,
  cityLine,
  beds,
  baths,
  sqft,
  pricePerSqft,
  propertyType,
  propertySubType,
  subdivisionName,
  city,
  listNumber,
  listOfficeName,
  badges,
  hasTour,
  tourUrl,
  tourLabel,
  onOpenTour,
  className,
  priority,
  ppsfBand,
}: {
  /** RETS ListingKey for the save heart; listNumber stands in when absent. */
  listingKey?: string | null
  href: string
  photoUrls: string[]
  price: number | null
  addressLine: string
  cityLine: string
  beds: number | null
  baths: number | null
  sqft: number | null
  pricePerSqft?: number | null
  propertyType: string | null
  propertySubType: string | null
  subdivisionName: string | null
  city: string | null
  listNumber: string | null
  listOfficeName?: string | null
  badges: Array<{ kind: V3ListingRowBadge; label: string }>
  hasTour: boolean
  tourUrl?: string | null
  tourLabel?: string
  onOpenTour?: () => void
  className?: string
  priority?: boolean
  /**
   * The price-per-square-foot spread of the homes CURRENTLY IN VIEW, so this
   * card can say where it sits among them (SITE-44). Null when the visible set
   * is too small to describe — the mark then renders its own empty state rather
   * than disappearing, so the rail keeps one rhythm.
   */
  ppsfBand?: PpsfBand | null
}) {
  const shareKind = publishListingShareKind({
    propertySubType,
    subdivisionName,
    city,
    listNumber,
  })
  const ask = formatPublishedSaleAsk({ price, propertyType })
  const meta: string[] = []
  if (beds != null) meta.push(`${Math.round(beds).toLocaleString()} bd`)
  if (baths != null) meta.push(`${Math.round(baths).toLocaleString()} ba`)
  if (sqft != null) meta.push(`${Math.round(sqft).toLocaleString()} sqft`)
  const publishedPpsf = publishListingSharePricePerSqft({
    propertyType,
    propertySubType,
    subdivisionName,
    city,
    listNumber,
    pricePerSqft: pricePerSqft ?? null,
  })
  if (publishedPpsf != null && publishedPpsf > 0) {
    meta.push(`$${Math.round(publishedPpsf).toLocaleString()}/sqft`)
  }

  const bandMark = (
    <SearchCompareMark
      band={ppsfBand}
      value={publishedPpsf != null && publishedPpsf > 0 ? publishedPpsf : null}
    />
  )

  return (
    <article className={cn(V3_ROOT_CLASS, 'v3-lrow', 'v3-lrow--split', className)}>
      <SplitCardMedia
        urls={photoUrls}
        tags={badges}
        hasTour={hasTour}
        tourUrl={tourUrl}
        onOpenTour={onOpenTour}
        addressLine={addressLine}
        fallbackName={city}
        priority={priority}
        tourLabel={tourLabel}
        sizes={SPLIT_CARD_MEDIA_SIZES_SPLIT}
        href={href}
        save={{ listingKey: listingKey ?? null, listNumber }}
      />
      <Link href={href} className="v3-lrow__copy">
        <span className="v3-lrow__price">{ask ?? '—'}</span>
        {shareKind ? <span className="v3-lrow__tag">{shareKind}</span> : null}
        {meta.length > 0 ? <span className="v3-lrow__meta">{meta.join(' · ')}</span> : null}
        {bandMark}
        <span className="v3-lrow__addr">{addressLine}</span>
        <span className="v3-lrow__city">{cityLine}</span>
        {listOfficeName ? <span className="v3-lrow__office">{listOfficeName}</span> : null}
      </Link>
    </article>
  )
}
